import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useApolloClient } from '@apollo/client';
import { Checkbox, FormControlLabel, Grid } from '@mui/material';
import { Autocomplete, Button, TextField } from '@totalsoft/rocket-ui';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { useTranslation } from 'react-i18next';
import { UPDATE_LEASING_INTEREST } from '../mutations';
import { LEASING_INTEREST_SAVED } from '../subscriptions';
import { leasingInterestModalVar } from 'features/leasingInterests/variables';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { leasingInterestValidator } from 'features/leasingInterests/edit/validators';
import { CURRENCIES_DICTIONARY_QUERY } from 'features/common/queries';
import { Prompt } from 'react-router-dom';
import { useToast } from 'hooks/toasts/useToast';
import { ADMIN_LEASING_INTERESTS_WRITE } from 'constants/rightsCodes';
import { emptyArray } from 'utils/constants';
import { usePermission } from 'providers/RightsHooks';

const EditComponent = ({ leasingInterest }) => {
  const client = useApolloClient();
  const addToast = useToast();
  const { t } = useTranslation();
  const canWrite = usePermission(ADMIN_LEASING_INTERESTS_WRITE);

  const [saving, setSaving] = useState(false);
  const [leasingInterestLens, dirtyInfo, resetLeasingInteresLens] = useChangeTrackingLens(leasingInterest);
  const [validation, validate] = useDirtyFieldValidation(leasingInterestValidator);

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  const { data: currenciesDictionaryData } = useQuery(CURRENCIES_DICTIONARY_QUERY);

  useEffect(() => {
    const { ...rest } = leasingInterest;
    resetLeasingInteresLens(rest);
  }, [leasingInterest, resetLeasingInteresLens]);

  useEffect(() => {
    validate(get(leasingInterestLens), dirtyInfo);
  }, [validate, dirtyInfo, leasingInterestLens]);

  const [updateLeasingInterest] = useMutation(UPDATE_LEASING_INTEREST, {
    onCompleted: _ => {},
    onError: () => {
      setSaving(false);
    }
  });

  const handleSave = useCallback(() => {
    const input = get(leasingInterestLens);
    if (!validate(input)) return;
    setSaving(true);
    // eslint-disable-next-line no-unused-vars
    const { currencyCode, ...rest } = input;
    updateLeasingInterest({ variables: { input: { ...rest } } });
  }, [leasingInterestLens, updateLeasingInterest, validate]);

  useSubscription(LEASING_INTEREST_SAVED, {
    onData: _ => {
      addToast(t('LeasingInterestType.Saved'), 'success');
      setSaving(false);
      leasingInterestModalVar({ open: false });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'leasingInterests' });
      client.cache.gc();
    }
  });

  const currencyChange = useCallback(
    value => {
      set(leasingInterestLens.currencyId)(value.id);
      set(leasingInterestLens.currencyCode)(value.code);
    },
    [leasingInterestLens.currencyId, leasingInterestLens.currencyCode]
  );

  return (
    <>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            disabled={!canWrite}
            fullWidth
            label={t('LeasingInterestType.Description')}
            required
            value={get(leasingInterestLens.interestTypeName) || ''}
            onChange={event => set(leasingInterestLens.interestTypeName)(event)}
            error={!isValid(validation.interestTypeName)}
            helperText={getErrors(validation.interestTypeName)}
            inputProps={{
              maxLength: 50
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            disabled={!canWrite}
            fullWidth
            label={t('LeasingInterestType.Code')}
            required
            value={get(leasingInterestLens.interestTypeCode) || ''}
            onChange={event => set(leasingInterestLens.interestTypeCode)(event)}
            error={!isValid(validation.interestTypeCode)}
            helperText={getErrors(validation.interestTypeCode)}
            inputProps={{
              maxLength: 25
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            disabled={!canWrite}
            fullWidth
            label={t('LeasingInterestType.Currency')}
            required
            valueKey={'id'}
            labelKey={'code'}
            value={{ id: leasingInterestLens.currencyId |> get, code: leasingInterestLens.currencyCode |> get }}
            onChange={currencyChange}
            options={
              currenciesDictionaryData && currenciesDictionaryData.currenciesDictionary
                ? currenciesDictionaryData.currenciesDictionary
                : emptyArray
            }
            error={!isValid(validation.currencyId)}
            helperText={getErrors(validation.currencyId)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            disabled={!canWrite}
            isNumeric
            fullWidth
            label={t('LeasingInterestType.Periodicity')}
            required
            decimalScale={0}
            value={get(leasingInterestLens.periodicity) || ''}
            onChange={set(leasingInterestLens.periodicity)}
            error={!isValid(validation.periodicity)}
            helperText={getErrors(validation.periodicity)}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            label={t('LeasingInterestType.Fixed')}
            control={
              <Checkbox
                disabled={!canWrite}
                checked={get(leasingInterestLens.isFixed) || false}
                onChange={event => set(leasingInterestLens.isFixed)(event.target.checked)}
                color='primary'
              />
            }
          />
        </Grid>
        <Grid container item xs={12} justifyContent='flex-end'>
          <Button onClick={handleSave} sx={{ mr: 1 }} disabled={saving || !canWrite}>
            {saving ? t('Saving') : t('Save')}
          </Button>
          <Button onClick={() => leasingInterestModalVar({ open: false })}>{t('Close')}</Button>
        </Grid>
      </Grid>
    </>
  );
};

EditComponent.propTypes = {
  leasingInterest: PropTypes.object.isRequired
};

export default EditComponent;
